// TRONA - Base Layout (Corporate Design)
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Navbar from 'components/navbar';
import Sidebar from 'components/sidebar';
import Footer from 'components/footer/Footer';
import ErrorBoundary from 'components/common/ErrorBoundary';
import routes from 'routes.js';
import { useNavigation } from 'hooks/useNavigation';
import { cn } from 'lib/utils';

const isPreviewAuthBypassEnabled = () =>
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_TRONA_PREVIEW_AUTH_BYPASS === 'true';

/**
 * Layout Base Unificado - Corporate Design
 * @param {Object} props - Props del layout
 * @param {string} props.layoutType - Tipo de layout: 'admin' o 'empresa'
 * @param {string} props.defaultRoute - Ruta por defecto para navegación
 */
export default function BaseLayout({
  layoutType = 'admin',
  defaultRoute = '/panel',
  ...rest
}) {
  const [open, setOpen] = React.useState(true);
  const [collapsed, setCollapsed] = React.useState(false);
  const { getRoutes } = useNavigation(routes);
  const { isAuthenticated, isLoading } = useAuth0();
  const previewAuthBypass = isPreviewAuthBypassEnabled();

  // Responsive sidebar handler
  React.useEffect(() => {
    const handleResize = () => {
      setOpen(window.innerWidth >= 1200);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate routes based on layout type
  const routeElements = getRoutes(routes, layoutType).map((route) => (
    <Route
      key={route.key}
      path={route.path}
      element={<ErrorBoundary>{route.element}</ErrorBoundary>}
    />
  ));

  // Set document direction
  document.documentElement.dir = 'ltr';

  // Determinar si mostrar sidebar y navbar
  const showSidebarAndNavbar =
    previewAuthBypass || (isAuthenticated && !isLoading);

  return (
    <div className="flex h-full w-full bg-neutral-50 dark:bg-neutral-950">
      {/* Sidebar - Desktop only (xl+) */}
      {showSidebarAndNavbar && (
        <div className="hidden xl:block">
          <Sidebar
            open={open}
            collapsed={collapsed}
            onClose={() => setOpen(false)}
            onToggleCollapse={() => setCollapsed(!collapsed)}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="min-h-screen flex-1 overflow-x-hidden">
        {/* Navbar - Fixed at top */}
        {showSidebarAndNavbar && <Navbar />}

        {/* Page Content */}
        <main
          className={cn(
            'min-h-screen bg-neutral-50 transition-colors duration-300',
            'dark:bg-[linear-gradient(180deg,#0d0f1a_0%,#151827_42%,#111827_100%)]',
            // Padding for navbar on mobile/tablet
            showSidebarAndNavbar && 'pt-16 md:pt-16',
            // Sidebar margin on desktop
            showSidebarAndNavbar && !collapsed && 'xl:ml-[304px]',
            showSidebarAndNavbar && collapsed && 'xl:ml-[88px]',
            // Remove top padding on desktop (sidebar layout)
            showSidebarAndNavbar && 'xl:pt-0'
          )}
        >
          <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
            <Routes>
              {routeElements}
              <Route
                path={`/${layoutType}`}
                element={<Navigate to={defaultRoute} replace />}
              />
            </Routes>
          </div>

          {/* Footer */}
          {showSidebarAndNavbar && (
            <div className="px-4 pb-6 sm:px-6 lg:px-8">
              <Footer />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
